import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Warehouse, Package } from "lucide-react";
import { fmtDate } from "@/lib/format";

/**
 * Iter-97 · Reserved-Stock Ledger.
 * Lists every `reserved` stock_movement grouped by warehouse × item × PR.
 */
export default function ReservedStockLedger() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/reports/reserved-stock").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return <div className="text-sm text-muted-foreground p-4">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="reserved-stock-ledger">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">// Reserved-stock ledger</div>
          <div className="text-sm text-muted-foreground">Every `reserved` stock movement · grouped for quick reconciliation</div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <Card className="rounded-sm p-3 border-amber-500/30 bg-amber-500/10">
          <div className="text-[10px] font-mono uppercase text-amber-800">Total reservations</div>
          <div className="text-2xl font-bold tabular-nums text-amber-900 mt-1">{data.total_reservations}</div>
        </Card>
        <Card className="rounded-sm p-3">
          <div className="text-[10px] font-mono uppercase text-muted-foreground mb-2 flex items-center gap-1.5"><Warehouse className="w-3 h-3" /> By warehouse</div>
          <div className="space-y-1 text-xs">
            {(data.by_warehouse || []).slice(0, 4).map((w) => (
              <div key={w.name} className="flex justify-between"><span>{w.name}</span><span className="font-mono tabular-nums">{w.qty}</span></div>
            ))}
            {(data.by_warehouse || []).length === 0 && <div className="text-muted-foreground text-center py-2">— no data —</div>}
          </div>
        </Card>
        <Card className="rounded-sm p-3">
          <div className="text-[10px] font-mono uppercase text-muted-foreground mb-2 flex items-center gap-1.5"><Package className="w-3 h-3" /> By item</div>
          <div className="space-y-1 text-xs">
            {(data.by_item || []).slice(0, 4).map((i) => (
              <div key={i.name} className="flex justify-between"><span className="truncate mr-2">{i.name}</span><span className="font-mono tabular-nums">{i.qty}</span></div>
            ))}
            {(data.by_item || []).length === 0 && <div className="text-muted-foreground text-center py-2">— no data —</div>}
          </div>
        </Card>
      </div>

      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Date</th><th>Warehouse</th><th>Item</th><th>SKU</th><th className="text-right">Reserved Qty</th><th>PR #</th></tr></thead>
          <tbody>
            {(data.rows || []).length === 0 && <tr><td colSpan="6" className="text-center py-10 text-muted-foreground">No reservations yet — Allocate a PR from the Stores Queue to see rows here.</td></tr>}
            {(data.rows || []).map((r) => (
              <tr key={r.movement_id} data-testid={`res-row-${r.movement_id}`}>
                <td className="text-xs">{fmtDate(r.date)}</td>
                <td className="text-sm">{r.warehouse_name}</td>
                <td>{r.item_name}</td>
                <td className="font-mono text-xs text-muted-foreground">{r.sku}</td>
                <td className="text-right tabular-nums font-medium">{r.reserved_qty} {r.unit}</td>
                <td className="font-mono text-xs">{r.pr_number}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
